import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";

export type DismissScope = "1h" | "4h" | "24h" | "permanent" | "suppress-forever";

interface Dismissal {
  until?: number;
  permanent?: boolean;
  suppressedForever?: boolean;
}

interface HintState {
  dismissed: Record<string, Dismissal>;
  globalSuppress: boolean;
  loaded: boolean;
}

const LS_KEY = "velaro_feature_hints_v1";
const GLOBAL_LS_KEY = "velaro_feature_hints_global_suppress";

function loadLocal(): HintState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, Dismissal>) : {};
    const globalSuppress = localStorage.getItem(GLOBAL_LS_KEY) === "1";
    return { dismissed: parsed, globalSuppress, loaded: true };
  } catch {
    return { dismissed: {}, globalSuppress: false, loaded: true };
  }
}

function persistLocal(state: HintState) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state.dismissed));
    localStorage.setItem(GLOBAL_LS_KEY, state.globalSuppress ? "1" : "0");
  } catch { /* storage quota or disabled */ }
}

function isSuppressed(d: Dismissal | undefined): boolean {
  if (!d) return false;
  if (d.permanent || d.suppressedForever) return true;
  if (d.until && Date.now() < d.until) return true;
  return false;
}

function scopeToDismissal(scope: DismissScope): Dismissal {
  switch (scope) {
    case "1h":   return { until: Date.now() + 60 * 60 * 1000 };
    case "4h":   return { until: Date.now() + 4 * 60 * 60 * 1000 };
    case "24h":  return { until: Date.now() + 24 * 60 * 60 * 1000 };
    case "permanent":        return { permanent: true };
    case "suppress-forever": return { suppressedForever: true };
  }
}

/**
 * Server-sync is lazy: writes always go to localStorage immediately, and
 * fire-and-forget to /UserHints when the messaging API exposes those endpoints.
 * On load, a 404 from the server means "no server-side storage yet" — we fall
 * back to localStorage silently.
 */
export function useHintDismiss() {
  const api = useApi();
  const [state, setState] = useState<HintState>(() => loadLocal());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.messaging.get("UserHints");
        if (!r.ok || cancelled) return;
        const body = await r.json() as { dismissed?: Record<string, Dismissal>; globalSuppress?: boolean };
        setState(prev => {
          const merged = { ...prev.dismissed, ...(body.dismissed ?? {}) };
          const next = {
            dismissed: merged,
            globalSuppress: body.globalSuppress ?? prev.globalSuppress,
            loaded: true
          };
          persistLocal(next);
          return next;
        });
      } catch { /* offline / endpoint not deployed — localStorage is the truth */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const isDismissed = useCallback((key: string) => {
    if (state.globalSuppress) return true;
    return isSuppressed(state.dismissed[key]);
  }, [state]);

  const dismiss = useCallback((key: string, scope: DismissScope) => {
    setState(prev => {
      const next = {
        ...prev,
        dismissed: { ...prev.dismissed, [key]: scopeToDismissal(scope) }
      };
      persistLocal(next);
      return next;
    });
    api.messaging.post(`UserHints/${encodeURIComponent(key)}/dismiss`, { scope })
      .catch(() => { /* best-effort */ });
  }, [api]);

  const reset = useCallback(() => {
    setState(prev => {
      const next = { ...prev, dismissed: {} };
      persistLocal(next);
      return next;
    });
    api.messaging.post("UserHints/reset", {}).catch(() => { /* best-effort */ });
  }, [api]);

  const setGlobalSuppress = useCallback((enabled: boolean) => {
    setState(prev => {
      const next = { ...prev, globalSuppress: enabled };
      persistLocal(next);
      return next;
    });
    api.messaging.post("UserHints/global-suppress", { enabled }).catch(() => { /* best-effort */ });
  }, [api]);

  return {
    isDismissed,
    dismiss,
    reset,
    globalSuppress: state.globalSuppress,
    setGlobalSuppress,
    dismissedCount: Object.keys(state.dismissed).filter(k => isSuppressed(state.dismissed[k])).length
  };
}
